import { type ResponseData, useApiQuery } from '@vrplatform/data';
import { sortBy, sum, toTitleCase } from '@vrplatform/utils';
import { useTrustReconcilationTableFilter } from './useTrustReconcilationTableFilter';
import { sortTrustReconcilationListingRows } from './useTrustReconcilationTableQuery.utils';

export const STORYBOOK_TRUST_RECONCILIATION_TABLE_QUERY_KEY =
  'trust-reconciliation-table';

type ApiRow =
  ResponseData<'get:/reports/trust-reconciliation'>['trustReconciliation']['data'][0];

export type TrustReconcilationTableQueryRow = {
  id: string;
  name: string;
  centTotal: number;
  type: 'accounts' | 'listings' | 'other';
  subRows: TrustReconcilationTableQueryRow[];
};

export type TrustReconcilationTableQuerySorting = 'name' | 'centTotal';

const formatRow = (
  row: ApiRow,
  sorting: TrustReconcilationTableQuerySorting,
  inverse: boolean
): TrustReconcilationTableQueryRow => {
  if (row.type === 'accounts') {
    return {
      id: row.name,
      name: row.name,
      type: row.type,
      centTotal: row.total * (inverse ? -1 : 1),
      subRows: sortBy(
        row.accounts.map<TrustReconcilationTableQueryRow>((account) => ({
          id: account.id,
          name: account.name,
          type: row.type,
          centTotal: account.total * (inverse ? -1 : 1),
          subRows: [],
        })),
        sorting,
        sorting === 'centTotal' ? 'desc' : 'asc'
      ),
    };
  }

  if (row.type === 'listings') {
    const isOwnerPayablesSection =
      row.name.trim().toLowerCase() === 'owner payables';

    return {
      id: row.name,
      name: row.name,
      type: 'other',
      centTotal: row.total * (inverse ? -1 : 1),
      subRows: sortTrustReconcilationListingRows(
        row.listings.map<TrustReconcilationTableQueryRow>((listing) => ({
          id: listing.id,
          name: listing.name,
          type: 'other',
          centTotal: listing.total * (inverse ? -1 : 1),
          subRows: [],
        })),
        sorting,
        {
          pinUnassignedListingFirst: isOwnerPayablesSection,
        }
      ),
    };
  }

  return {
    id: row.name,
    name: row.name,
    type: 'other',
    centTotal: row.total * (inverse ? -1 : 1),
    subRows: sortBy(
      row.occupancyStatuses.map<TrustReconcilationTableQueryRow>(
        (occupancyStatus) => ({
          id: occupancyStatus.status,
          name: toTitleCase(occupancyStatus.status),
          type: 'other',
          centTotal: occupancyStatus.total * (inverse ? -1 : 1),
          subRows: [],
        })
      ),
      sorting
    ),
  };
};

export const useTrustReconcilationTableQuery = (
  sorting: TrustReconcilationTableQuerySorting
) => {
  const { filter } = useTrustReconcilationTableFilter();

  return useApiQuery(
    async ($api, signal, args) => {
      const result = await $api.GET('/reports/trust-reconciliation', {
        params: {
          query: {
            endDate: args.filter.endDate,
            listingId: args.filter.listingId ?? undefined,
          },
        },
        signal,
      });

      if (result.error) throw result.error;

      const accounts: TrustReconcilationTableQueryRow[] = [];
      const liabilities: TrustReconcilationTableQueryRow[] = [];

      const rows = result.data.trustReconciliation.data;

      for (const row of rows) {
        if (
          row.type === 'accounts' &&
          (row.name === 'Bank Accounts' || row.name === 'Other Assets')
        ) {
          accounts.push(formatRow(row, args.sorting, false));
        } else {
          liabilities.push(
            formatRow(
              row,
              args.sorting,
              row.name?.toLowerCase() !== 'unassigned accounts'
            )
          );
        }
      }

      return {
        rows: [
          {
            id: 'total',
            name: result.data.trustReconciliation.name,
            type: 'other',
            centTotal: result.data.trustReconciliation.total,
            subRows: [
              {
                id: 'accounts',
                name: 'Assets',
                type: 'accounts' as const,
                centTotal: sum(accounts.map((account) => account.centTotal)),
                subRows: accounts.sort((a, b) => {
                  const nameA = a.name.toLowerCase();
                  const nameB = b.name.toLowerCase();

                  if (nameA === 'bank accounts') return -1;
                  if (nameB === 'bank accounts') return 1;

                  if (nameA === 'other assets') return -1;
                  if (nameB === 'other assets') return 1;

                  return 0;
                }),
              },
              {
                id: 'liabilities',
                name: 'Liabilities',
                type: 'other' as const,
                centTotal: sum(
                  liabilities.map((liability) => liability.centTotal)
                ),
                subRows: liabilities.sort((a, b) => {
                  const nameA = a.name.toLowerCase();
                  const nameB = b.name.toLowerCase();

                  const order: Record<string, number> = {
                    'guest receivables & deposits': 0,
                    'taxes payable': 1,
                    'other liabilities': 2,
                    'owner payables': 3,
                    'manager payable': 4,
                  };

                  return order[nameA] - order[nameB];
                }),
              },
            ],
          },
        ] satisfies TrustReconcilationTableQueryRow[],
      };
    },
    {
      storybookQueryKey: STORYBOOK_TRUST_RECONCILIATION_TABLE_QUERY_KEY,
      queryGroup: 'reports',
      uniqueQueryKey: 'trust-reconciliation-report',
      queryKeys: ['journalEntries', 'reports'],
      variables: {
        filter,
        sorting,
      },
    }
  );
};
